import {useEffect, useMemo, useRef, useState} from 'react'
import {Badge, Button, Form, Modal} from 'react-bootstrap'

import {useIntl} from 'react-intl'
import {useTelephony} from '../../hooks/useTelephony'
import {
  DISPOSITION_LABELS,
  EM_DASH_SEP,
  TELEPHONY_MESSAGES as T,
} from '../../helpers/telephonyMessages'
import {
  TELEPHONY_VICIDIAL_DISPOSITION_EVENT,
  TelephonyVicidialDispositionDetail,
} from '../../types/telephony'

interface CompletedVicidialCallContext {
  key: string
  status: string
  callId: string
  leadId: string
  campaignId: string
  phoneNumber: string
  vendorLeadCode: string
  firstName: string
  lastName: string
  email: string
  sessionId: string
  checkedAt: number | null
}

interface VicidialDispositionOption {
  code: string
  schedulesCallback?: boolean
}

/** El `code` viaja a PolicyDial; la etiqueta sale de `DISPOSITION_LABELS`. */
const VICIDIAL_DISPOSITION_OPTIONS: VicidialDispositionOption[] = [
  {code: 'SALE'},
  {code: 'DEC'},
  {code: 'NINT'},
  {code: 'NAPL'},
  {code: 'CALLBK', schedulesCallback: true},
  {code: 'NANS'},
  {code: 'N'},
  {code: 'B'},
  {code: 'A'},
  {code: 'VMAIL'},
  {code: 'HUPNR'},
  {code: 'DAIR'},
  {code: 'DC'},
  {code: 'WRNUM'},
  {code: 'DNC'},
  {code: 'XFER'},
  {code: 'CHATR'},
  {code: 'CHATNR'},
  {code: 'EMAIR'},
  {code: 'EMAINR'},
  {code: 'EMAIF'},
]

const VICIDIAL_CALLBACK_DISPOSITIONS = new Set(
  VICIDIAL_DISPOSITION_OPTIONS.filter((option) => option.schedulesCallback).map(
    (option) => option.code
  )
)

const hasMeaningfulValue = (value?: string | null) => {
  const normalized = String(value || '').trim().toUpperCase()
  return Boolean(normalized && !['0', 'NONE', 'NULL', 'N/A'].includes(normalized))
}

const buildDisplayName = (firstName?: string | null, lastName?: string | null) =>
  [String(firstName || '').trim(), String(lastName || '').trim()]
    .filter((value) => hasMeaningfulValue(value))
    .join(' ')

const isDispositionPendingState = (status?: string, realTimeSubStatus?: string) => {
  const statusValue = String(status || '').trim().toUpperCase()
  const subStatusValue = String(realTimeSubStatus || '').trim().toUpperCase()
  return (
    statusValue === 'DEAD' ||
    subStatusValue === 'DEAD' ||
    subStatusValue.includes('DEAD') ||
    statusValue === 'DISPO' ||
    subStatusValue === 'DISPO'
  )
}

const hasCallContext = (input: {
  status?: string
  realTimeSubStatus?: string
  callId?: string
  leadId?: string
  phoneNumber?: string
  vendorLeadCode?: string
}) => {
  const statusValue = String(input.status || '').trim().toUpperCase()
  const subStatusValue = String(input.realTimeSubStatus || '').trim().toUpperCase()
  if (isDispositionPendingState(statusValue, subStatusValue)) {
    return false
  }

  return (
    hasMeaningfulValue(input.callId) ||
    hasMeaningfulValue(input.leadId) ||
    hasMeaningfulValue(input.phoneNumber) ||
    hasMeaningfulValue(input.vendorLeadCode) ||
    ['INCALL', 'LIVE', 'QUEUE', 'RING'].some((marker) => statusValue.includes(marker)) ||
    ['INCALL', 'LIVE', 'QUEUE', 'RING'].some((marker) => subStatusValue.includes(marker))
  )
}

const isIdleVicidialState = (status?: string, realTimeSubStatus?: string) => {
  const normalized = String(status || '').trim().toUpperCase()
  return isDispositionPendingState(status, realTimeSubStatus) || ['', 'PAUSED', 'READY', 'CLOSER', 'LOGOUT'].includes(normalized)
}

const buildCallKey = (input: {
  sessionId?: string
  callId?: string
  leadId?: string
  phoneNumber?: string
  vendorLeadCode?: string
  campaignId?: string
  checkedAt?: number | null
}) =>
  [
    String(input.sessionId || '').trim(),
    String(input.callId || input.leadId || input.vendorLeadCode || input.phoneNumber || '').trim(),
    String(input.campaignId || '').trim(),
    String(input.checkedAt || '').trim(),
  ].join('|')

export const TelephonyDispositionModal: React.FC = () => {
  const intl = useIntl()
  const telephony = useTelephony()
  const [show, setShow] = useState(false)
  const [statusCode, setStatusCode] = useState('')
  const [comments, setComments] = useState('')
  const [callbackDateTime, setCallbackDateTime] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [context, setContext] = useState<CompletedVicidialCallContext | null>(null)
  const activeCallRef = useRef<CompletedVicidialCallContext | null>(null)
  const lastShownCallKeyRef = useRef('')

  const sessionSnapshot = useMemo(
    () => ({
      status: telephony.vicidialSession.status,
      realTimeSubStatus: telephony.vicidialSession.realTimeSubStatus,
      callId: telephony.vicidialSession.callId,
      leadId: telephony.vicidialSession.leadId,
      campaignId: telephony.vicidialSession.campaignId,
      phoneNumber: telephony.vicidialSession.phoneNumber,
      vendorLeadCode: telephony.vicidialSession.vendorLeadCode,
      firstName: telephony.vicidialSession.firstName,
      lastName: telephony.vicidialSession.lastName,
      email: telephony.vicidialSession.email,
      sessionId: telephony.vicidialSession.sessionId,
      checkedAt: telephony.vicidialSession.checkedAt,
    }),
    [
      telephony.vicidialSession.callId,
      telephony.vicidialSession.campaignId,
      telephony.vicidialSession.checkedAt,
      telephony.vicidialSession.email,
      telephony.vicidialSession.firstName,
      telephony.vicidialSession.lastName,
      telephony.vicidialSession.leadId,
      telephony.vicidialSession.phoneNumber,
      telephony.vicidialSession.realTimeSubStatus,
      telephony.vicidialSession.sessionId,
      telephony.vicidialSession.status,
      telephony.vicidialSession.vendorLeadCode,
    ]
  )

  useEffect(() => {
    const handleDispositionEvent = (event: Event) => {
      const customEvent = event as CustomEvent<TelephonyVicidialDispositionDetail>
      const detail = customEvent.detail
      if (!detail?.key || detail.key === lastShownCallKeyRef.current) {
        return
      }

      lastShownCallKeyRef.current = detail.key
      activeCallRef.current = null
      setContext({
        key: detail.key,
        status: detail.status,
        callId: detail.callId,
        leadId: detail.leadId,
        campaignId: detail.campaignId,
        phoneNumber: detail.phoneNumber,
        vendorLeadCode: detail.vendorLeadCode,
        firstName: detail.firstName,
        lastName: detail.lastName,
        email: detail.email,
        sessionId: detail.sessionId,
        checkedAt: detail.checkedAt,
      })
      setStatusCode('')
      setComments('')
      setCallbackDateTime('')
      setShow(true)
    }

    window.addEventListener(TELEPHONY_VICIDIAL_DISPOSITION_EVENT, handleDispositionEvent as EventListener)

    return () => {
      window.removeEventListener(
        TELEPHONY_VICIDIAL_DISPOSITION_EVENT,
        handleDispositionEvent as EventListener
      )
    }
  }, [])

  useEffect(() => {
    const currentHasCallContext = hasCallContext(sessionSnapshot)

    if (currentHasCallContext) {
      activeCallRef.current = {
        key: buildCallKey(sessionSnapshot),
        status: String(sessionSnapshot.status || '').trim().toUpperCase(),
        callId: String(sessionSnapshot.callId || '').trim(),
        leadId: String(sessionSnapshot.leadId || '').trim(),
        campaignId: String(sessionSnapshot.campaignId || '').trim(),
        phoneNumber: String(sessionSnapshot.phoneNumber || '').trim(),
        vendorLeadCode: String(sessionSnapshot.vendorLeadCode || '').trim(),
        firstName: String(sessionSnapshot.firstName || '').trim(),
        lastName: String(sessionSnapshot.lastName || '').trim(),
        email: String(sessionSnapshot.email || '').trim(),
        sessionId: String(sessionSnapshot.sessionId || '').trim(),
        checkedAt: sessionSnapshot.checkedAt || null,
      }
      return
    }

    if (!activeCallRef.current || !isIdleVicidialState(sessionSnapshot.status, sessionSnapshot.realTimeSubStatus)) {
      return
    }

    const completedCall = activeCallRef.current
    activeCallRef.current = null

    if (!completedCall.key || completedCall.key === lastShownCallKeyRef.current) {
      return
    }

    lastShownCallKeyRef.current = completedCall.key
    setContext(completedCall)
    setStatusCode('')
    setComments('')
    setCallbackDateTime('')
    setShow(true)
  }, [sessionSnapshot])

  const handleClose = () => {
    if (submitting) return
    setShow(false)
  }

  const handleSubmit = async () => {
    if (!context) return

    // Close the modal immediately so the user can move on. The VICIdial dispo
    // + auto-resume round-trip can take 5-10s — keeping the modal in "Saving"
    // for that long felt broken. On failure we clear the call-key so the
    // dispo modal re-opens for the same call and the user can retry.
    const submittedKey = context.key
    setShow(false)
    setSubmitting(true)
    try {
      const response = await telephony.setVicidialDisposition({
        statusCode,
        callbackDateTime,
        callbackComments: comments,
      })

      if (!response && lastShownCallKeyRef.current === submittedKey) {
        lastShownCallKeyRef.current = ''
      }
    } finally {
      setSubmitting(false)
    }
  }

  const displayName = buildDisplayName(context?.firstName, context?.lastName)

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop='static'
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>{intl.formatMessage(T.dispositionTitle)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='text-muted mb-3'>{intl.formatMessage(T.dispositionIntro)}</p>
        {displayName && <div className='fw-bold text-dark fs-6 mb-2'>{displayName}</div>}
        <div className='d-flex flex-wrap gap-2 mb-3'>
          {context?.phoneNumber && (
            <Badge bg='light' text='dark' className='border'>
              {intl.formatMessage(T.badgePhone, {value: context.phoneNumber})}
            </Badge>
          )}
          {context?.email && hasMeaningfulValue(context.email) && (
            <Badge bg='light' text='dark' className='border'>
              {intl.formatMessage(T.badgeEmail, {value: context.email})}
            </Badge>
          )}
          {context?.vendorLeadCode && hasMeaningfulValue(context.vendorLeadCode) && (
            <Badge bg='light' text='dark' className='border'>
              {intl.formatMessage(T.badgeVendorId, {value: context.vendorLeadCode})}
            </Badge>
          )}
          {context?.leadId && hasMeaningfulValue(context.leadId) && (
            <Badge bg='light' text='dark' className='border'>
              {intl.formatMessage(T.badgeLeadId, {value: context.leadId})}
            </Badge>
          )}
          {context?.campaignId && (
            <Badge bg='light' text='dark' className='border'>
              {intl.formatMessage(T.badgeCampaign, {value: context.campaignId})}
            </Badge>
          )}
          {context?.sessionId && (
            <Badge bg='light' text='dark' className='border'>
              {intl.formatMessage(T.badgeSession, {value: context.sessionId})}
            </Badge>
          )}
        </div>
        <Form.Group className='mb-3'>
          <Form.Label>{intl.formatMessage(T.dispositionCode)}</Form.Label>
          <Form.Select
            value={statusCode}
            onChange={(event) => setStatusCode(event.target.value)}
            autoFocus
            disabled={submitting}
          >
            <option value=''>{intl.formatMessage(T.dispositionSelect)}</option>
            {VICIDIAL_DISPOSITION_OPTIONS.map((option) => (
              <option key={option.code} value={option.code}>
                {option.code}
                {EM_DASH_SEP}
                {intl.formatMessage(DISPOSITION_LABELS[option.code])}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className='mb-3'>
          <Form.Label>{intl.formatMessage(T.comments)}</Form.Label>
          <Form.Control
            as='textarea'
            rows={3}
            value={comments}
            onChange={(event) => setComments(event.target.value)}
            placeholder={intl.formatMessage(T.dispositionNotes)}
            disabled={submitting}
          />
        </Form.Group>
        {VICIDIAL_CALLBACK_DISPOSITIONS.has(statusCode) && (
          <Form.Group>
            <Form.Label>{intl.formatMessage(T.callbackDateTime)}</Form.Label>
            <Form.Control
              type='datetime-local'
              value={callbackDateTime}
              onChange={(event) => setCallbackDateTime(event.target.value)}
              disabled={submitting}
            />
            <Form.Text muted>
              {intl.formatMessage(T.callbackRequired, {code: statusCode})}
            </Form.Text>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='primary'
          onClick={handleSubmit}
          disabled={submitting || !String(statusCode || '').trim()}
        >
          {submitting
            ? intl.formatMessage(T.saving)
            : intl.formatMessage(T.submitDisposition)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
